import { type Dispatch, type FC, type SetStateAction, useEffect } from "react";

import { TableCell, TableRow } from "~/common/components/Table/TableElements";
import { CellWithSubtitle } from "~/features/productDetails/components/DataTables/CellWithSubtitle";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { t } from "i18next";
import { getStatusData, type RowProps } from "~/features/orders/orderList/components/OrderLineTable";

import { ParamsGrid } from "~/common/components/ParamsGrid";
import { WeightFormat } from "~/domains/product/components/WeightFormat";
import Chip from "~/common/components/Chip/Chip";
import { OrderLineItemElements } from "~/features/orders/common/components/OrderLineItemElements";
import { useSendOrderFeedback } from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/useSendOrderFeedback";
import { CreateCaseElement } from "~/features/orders/common/components/CreateCaseElement";

interface Props extends RowProps {
  index: number;
  subEntry: OrderLineItemData;
  contactSchaefflerPermission: boolean;
  setSnackbarData?: Dispatch<SetStateAction<{ open: boolean; message: string }>>;
  setHasCreatedCaseSpinner: Dispatch<SetStateAction<boolean>>;
}

const OrderSubLineTable: FC<Props> = ({
  index,
  subEntry,
  contactSchaefflerPermission,
  data,
  orderData,
  lineItemIndex,
  soNumber,
  poNumber,
  setSnackbarData,
  setHasCreatedCaseSpinner,
}) => {
  const [popups, open, userHasCreatedCase] = useSendOrderFeedback(
    data,
    orderData,
    lineItemIndex,
    soNumber,
    poNumber,
    setSnackbarData,
    subEntry.lineNo,
  );

  useEffect(() => {
    if (userHasCreatedCase) {
      setHasCreatedCaseSpinner(true);
    }
  }, [userHasCreatedCase]);

  return (
    <>
      <TableRow>
        <TableCell className="p-0 pt-16" colSpan={10} />
      </TableRow>
      <TableRow key={index} className={"scha__order-list__deliveries-row"}>
        <TableCell />
        <TableCell>{subEntry.lineNo}</TableCell>
        <TableCell>
          <CellWithSubtitle title={subEntry.confirmedQty ?? "-"} subtitle={subEntry.qty} lightHeader={true} />
        </TableCell>
        <TableCell></TableCell>
        <TableCell>
          <CellWithSubtitle
            title={formatNullableDate(subEntry.deliveryDate, DateFormatType.DATE)}
            subtitle={formatNullableDate(subEntry.requestedDeliveryDate, DateFormatType.DATE)}
            lightHeader={true}
          />
        </TableCell>
        <TableCell>
          <CellWithSubtitle
            title={<WeightFormat weight={subEntry.weight} />}
            subtitle={t("common.product.perPiece", { count: 1 })}
            lightHeader={true}
          />
        </TableCell>
        <TableCell>
          <OrderLineItemElements.NetPrice data={subEntry} />
        </TableCell>
        <TableCell>
          <OrderLineItemElements.TotalPrice data={subEntry} />
          <OrderLineItemElements.Surcharge data={subEntry} className={"mt-2"} />
        </TableCell>
        <TableCell>
          <CellWithSubtitle
            title={<Chip key={"chip"}>{t(subEntry.status)}</Chip>}
            lightHeader={true}
            subtitle={
              <ParamsGrid
                key={`invoice-${subEntry.lineNo}`}
                params={getStatusData(t, subEntry)}
                noColumnFlex={true}
              />
            }
          />
        </TableCell>
        <TableCell />
      </TableRow>
      {contactSchaefflerPermission && (
        <TableRow className={"scha__order-list__deliveries-case-row"}>
          <TableCell />
          <TableCell colSpan={9}>
            <CreateCaseElement openPopup={open} />
          </TableCell>
        </TableRow>
      )}
      {index === (data.subEntries && data.subEntries?.length - 1) && (
        <TableRow className={"scha__order-list__deliveries-row-spacer"} />
      )}
      {popups}
    </>
  );
};

export default OrderSubLineTable;
